import { Span } from '@opentelemetry/api'
import axios from 'axios'
import { z } from 'zod'
import { ServiceError, FlightNotFoundError, MetaCompoundError, MetaError } from '@/lib/errors'
import { StructuredLoggerOptions, StructuredLogger } from '@/lib/logger/types'

export function factoryWithDefaultOptions<T extends any>(
  defaultOptions: StructuredLoggerOptions,
  factory: (logger: T, options: StructuredLoggerOptions) => StructuredLogger
): (logger: T, options?: Partial<StructuredLoggerOptions>) => StructuredLogger {
  return (logger, options) => factory(logger, { ...defaultOptions, ...options })
}

export function getErrorMetadata(
  error: unknown,
  iteration: number = 0
): Record<string, any> | undefined {
  if (iteration > 50) {
    return undefined
  }
  if (error instanceof ServiceError) {
    return {
      id: error.id,
      type: 'service-error',
      ...error.metadata,
      error: getErrorMetadata(error.error, iteration + 1),
    }
  }
  if (error instanceof FlightNotFoundError) {
    return {
      type: 'flight-not-found-error',
      ...error.metadata,
    }
  }
  if (error instanceof MetaCompoundError) {
    return {
      type: 'meta-compound-error',
      ...error.metadata,
      errors: Object.fromEntries(
        Object.entries(error.errors).map(([key, value]) => [
          key,
          getErrorMetadata(value, iteration + 1),
        ])
      ),
    }
  }
  if (error instanceof MetaError) {
    return {
      type: 'meta-error',
      ...error.metadata,
      error: getErrorMetadata(error.error, iteration + 1),
    }
  }
  if (axios.isAxiosError(error)) {
    return {
      type: 'axios-error',
      code: error.code,
      message: error.message,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
          }
        : undefined,
    }
  }
  if (error instanceof z.ZodError) {
    return {
      type: 'zod-error',
      issues: error.issues,
      fields: error.issues.map((issue) => `${issue.path.join('.')} : ${issue.message}`),
      message: error.message,
      cause: error.cause,
      stack: error.stack,
    }
  }
  if (error instanceof AggregateError) {
    return {
      type: 'aggregate-error',
      errors: error.errors.map(getErrorMetadata),
    }
  }
  if (error instanceof Error) {
    return { type: 'error', message: error.message, name: error.name, stack: error.stack }
  }
  if (error === undefined) {
    return undefined
  }
  try {
    const raw = JSON.parse(JSON.stringify(error, Object.getOwnPropertyNames(error)))
    return { type: 'unknown', raw }
  } catch (e) {
    return { type: 'unknown', raw: error, message: 'This should never happen' }
  }
}

export function flattenObject(
  obj: any,
  path: string[] = []
): Record<string, number | string | boolean> {
  let flattened: Record<string, number | string | boolean> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
      flattened[`${path.join('.')}.${key}`] = value
    } else if (typeof value === 'object' && value !== null) {
      flattened = { ...flattenObject(value, [...path, key]), ...flattened }
    }
  }
  return flattened
}

export function addAttributesToSpan(span: Span, obj: any) {
  for (const [key, value] of Object.entries(flattenObject(obj))) {
    span.setAttribute(key, value)
  }
  span.setAttribute('instrumentationlibrary.name', 'structured-logger')
}
